/**
 * feedback.js —— 工具页 AI 助手 / 反馈组件（全站通用）
 * ---------------------------------------------------------------------------
 * 方案：docs/tools-site/FEEDBACK-AI-PLAN.md
 * 模板：app/tools/view/item/feedback.html（仅工具详情页 include，脚本也仅该页引入）
 *
 * 产品定位（2026-09-14 定稿）：AI 不是「答不上来才出现的兜底」，而是**常驻在结果旁边**的助手：
 *   ① AI 看得见结果 —— 提交时抓「输入 + 工具当前输出」快照，抱怨「结果不对」才有得救；
 *   ② 多轮追问 —— 会话流，历史问答随请求带上，不是一次问答就死；
 *   ③ 结果区入口 —— 在 .tool-result 注入「AI 解读结果」（运行时注入，不改 171 个 partial）；
 *   ④ 回答可评价 —— 👍/👎 落库（feedback/rate），这是唯一能证伪提示词的数据。
 *
 * 状态机：
 *   初始 → 解决了 → 终态（仅一句感谢，POST solved=1，不给再反馈入口）
 *        → 没解决 → 首问表单（快捷原因标签）→ 提交
 *          → 有 AI 回答 → 会话流（第 1 轮 + 追问框 + 评价）
 *          → 无 AI 回答 → 感谢态（只收集，文案如实说明 + 可再反馈一条）
 *   追问：会话流内任意轮次，携带最近 3 轮历史；失败只 toast，不破坏已有轮次。
 *   安全：AI 回答一律 textContent 写入；端点取自模板注入；历史条数与长度服务端兜底。
 */
(function (global) {

  /* 反馈组件文案：按站点语言取语言包 ui.fb，逐级回退，最后才用内置默认值。
     回退链：当前语言 → 站点组基准语言（window.TOOLS_FALLBACK_LANG，海外站为 'en'）
             → 内置默认值（中文，仅当两级语言包都缺该键才走到）。
     刻意不硬编码 zh-CN 兜底：海外站新增语言（mx/id）尚未翻译时，必须回退到组基准英文，
     否则整块反馈组件会冒中文——这与 ui.js 的 dict() 是同一条口径，改一处要同步两处。
     文案在 data/tools-site/locales/{site}/_ui.json 的 ui.fb 段（服务端按页内联注入）；
     新增文案请同步 zh 与 en 两个站点。 */
  function T(key, fallback) {
    var packs = window.ToolI18n || {};
    var langs = [window.TOOLS_LANG, window.TOOLS_FALLBACK_LANG];
    for (var i = 0; i < langs.length; i++) {
      if (!langs[i]) { continue; }
      var ui = (packs[langs[i]] || {}).ui || {};
      var v = (ui.fb || {})[key];
      if (typeof v === 'string' && v !== '') { return v; }
    }
    return fallback;
  }

  /* 服务端失败响应 → 用户可见文案。
     优先用服务端返回的稳定错误码 code 查语言包（ui.fb[code]，如 fbTooFrequent）；
     **有 code 时刻意不回落服务端的 msg** —— msg 是中文原文，海外站不能把它弹给用户。
     无 code（响应没带上、非本组件来源）时：msg 只允许**中文站**展示，
     海外站一律走语言包的兜底键（该键必然存在：门禁会校验本文件每个字面量键的引用），
     这样「海外站全页不出现中文」在任何分支都成立。 */
  function failMsg(res, fallbackKey) {
    var code = (res && res.code) ? String(res.code) : '';
    if (code) { return T(code, '') || T(fallbackKey, ''); }
    if (res && res.msg && /^zh/i.test(window.TOOLS_LANG || '')) { return String(res.msg); }
    return T(fallbackKey, '');
  }
  'use strict';

  var doc = global.document;
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function byHook(section, hook) { return section.querySelector('[data-feedback-' + hook + ']'); }
  function allHook(section, hook) { return section.querySelectorAll('[data-feedback-' + hook + ']'); }

  function el(tag, cls) {
    var node = doc.createElement(tag);
    if (cls) { node.className = cls; }
    return node;
  }

  function span(cls, text) {
    var node = el('span', cls);
    node.textContent = text;
    return node;
  }

  /** sprite 图标（图标名是代码里的静态串，无用户数据） */
  function iconEl(name) {
    var svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'tool-icon');
    svg.setAttribute('aria-hidden', 'true');
    var use = doc.createElementNS(SVG_NS, 'use');
    use.setAttribute('href', '#i-' + name);
    svg.appendChild(use);
    return svg;
  }

  /** 三点脉冲：生成中的动效。放在回答位里（内容出现的原地），不再单挂一行等待提示 */
  function dotsEl() {
    var wrap = el('span', 'tool-feedback-dots');
    wrap.setAttribute('aria-hidden', 'true');
    wrap.appendChild(el('span', ''));
    wrap.appendChild(el('span', ''));
    wrap.appendChild(el('span', ''));
    return wrap;
  }

  function toast(msg) {
    var UI = global.ToolUI || {};
    if (UI.toast) { UI.toast(msg); }
  }

  /**
   * 「为什么没有 AI 回答」的如实文案（后端回传 skip）。
   *
   * 必须把「额度用完」和「AI 坏了」分开说：在用户眼里这两种原本长得一模一样
   * ——都只得到一句「已收到反馈」，于是合理地判断成「AI 功能坏了」（2026-09-14 用户实际踩到，
   * 原因是他连续测试撞上了每 IP 每小时 5 次的公共额度）。
   */
  function skipMsg(skip) {
    if (skip === 'quota_ip') {
      return T('quotaHour', 'AI 助手这一小时的公共额度用完了，你的反馈已收到；过一会儿可以继续追问。');
    }
    if (skip === 'quota_day') {
      return T('quotaDay', 'AI 助手今天的公共额度用完了，你的反馈已收到；明天可继续追问。');
    }
    if (skip === 'ai_fail') {
      return T('aiFail', 'AI 暂时没能回答，你的反馈已收到；稍后再试可以继续问。');
    }
    return '';   // no_config 等：保持原来的「已收到反馈」措辞
  }

  function textOf(node) {
    return node ? String(node.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  /** 空结果占位符（—、-、--）不算结果，别喂给 AI 当数据 */
  function isEmptyValue(v) {
    return v === '' || v === '—' || v === '-' || v === '--';
  }

  /**
   * 抓工具当前输出：主结果（.tool-result-value + 它的标签）+ 附加统计（.tool-stat）。
   * 这是「AI 看得见结果」的落点 —— 用户抱怨「算出来不对」时，没有这段只能泛泛而谈。
   */
  function collectResults(section) {
    var out = [];
    var seen = {};
    function push(name, valueNode) {
      var v = textOf(valueNode);
      if (isEmptyValue(v)) { return; }
      var key = name + '|' + v;
      if (seen[key]) { return; }
      seen[key] = 1;
      out.push((name ? name + '=' : '') + v.slice(0, 80));
    }

    var results = doc.querySelectorAll('.tool-result');
    for (var i = 0; i < results.length && out.length < 8; i++) {
      if (section.contains(results[i])) { continue; }
      var value = results[i].querySelector('.tool-result-value');
      if (!value) { continue; }
      // 标签取 .tool-result-label 整块：各 partial 里挂标签的属性名不统一
      // （data-result-label / data-label="result-x" 都有），整块取文本最省事也最稳
      push(textOf(results[i].querySelector('.tool-result-label')) || T('result', '结果'), value);
    }

    var stats = doc.querySelectorAll('.tool-stat');
    for (var j = 0; j < stats.length && out.length < 12; j++) {
      if (section.contains(stats[j])) { continue; }
      push(textOf(stats[j].querySelector('.tool-stat-name')), stats[j].querySelector('.tool-stat-value'));
    }
    return out;
  }

  /** 提交给后端的上下文：输入快照 + 结果快照（后端 CONTEXT_MAX 兜底截断） */
  function collectContext(section) {
    try {
      var parts = [];
      var inputs = [];
      var root = doc.querySelector('.tool-card--tool') || doc.body;
      var els = root.querySelectorAll('input, select, textarea');
      for (var i = 0; i < els.length && inputs.length < 12; i++) {
        var el0 = els[i];
        /* hidden 默认跳过（多为状态桩）；带 data-ai-ctx 是工具作者显式声明
           「这个隐藏字段要给 AI 看」（如情境测评的选择明细），才纳入上下文 */
        if (!el0.id || (el0.type === 'hidden' && !el0.hasAttribute('data-ai-ctx')) || el0.disabled) { continue; }
        if (section.contains(el0)) { continue; }   // 排除反馈组件自身
        var v = String(el0.value == null ? '' : el0.value).trim();
        if (v === '') { continue; }
        var lb = doc.querySelector('label[for="' + el0.id + '"]');
        /* data-ai-ctx 字段放宽到 300 字：多题选择的拼接文本 60 字装不下 */
        inputs.push((lb ? textOf(lb) : el0.id) + '=' + v.slice(0, el0.hasAttribute('data-ai-ctx') ? 300 : 60));
      }
      if (inputs.length) { parts.push(T('ctxInput', '输入：') + inputs.join(T('ctxSep', '；'))); }

      var outs = collectResults(section);
      if (outs.length) { parts.push(T('ctxOutput', '结果：') + outs.join(T('ctxSep', '；'))); }

      /* 1200：实测情境测评全状态（六题选择 + 清单勾选 + 事实 + 结果）逼近 900 帽沿，
         结果区会被尾部吃掉；后端 CONTEXT_MAX=1500 兜底，这里留足余量 */
      return parts.join('\n').slice(0, 1200);
    } catch (e) { return ''; }
  }

  function pageSlug() {
    var segs = String(global.location && global.location.pathname || '').split('/');
    for (var i = segs.length - 1; i >= 0; i--) {
      if (segs[i] !== '') { return segs[i]; }
    }
    return '';
  }

  /** fetch 优先（冒烟可 mock），无 fetch 环境降级 XHR */
  function postJSON(url, data) {
    var body = JSON.stringify(data);
    if (typeof global.fetch === 'function') {
      return global.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body
      }).then(function (res) { return res.json(); });
    }
    return new Promise(function (resolve, reject) {
      try {
        var xhr = new global.XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.onload = function () {
          try { resolve(JSON.parse(xhr.responseText)); } catch (e) { reject(e); }
        };
        xhr.onerror = function () { reject(new Error('network')); };
        xhr.send(body);
      } catch (e) { reject(e); }
    });
  }

  /**
   * 流式 POST（SSE）：逐段回调 onChunk，最终 resolve 出与服务端 done/error 同形状的对象。
   * 事件协议：meta{id} / chunk{text}* / done{ok,answer,aiOk,id} / error{ok:false,msg,answer?}
   *
   * 解析要点：网络分片**不保证按行/按事件对齐**，必须自己攒缓冲按 "\n\n" 切事件；
   * 少这一步就会出现「JSON 被切成两半 → parse 失败 → 回答少一截」的偶发 bug。
   * 另：若服务端因早退（频控 429 / 未开启 404）返回的仍是普通 JSON，整包兜底解析一次。
   */
  function postStream(url, data, onChunk, onMeta) {
    var payload = data;
    payload.stream = 1;
    return global.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res || !res.body || typeof res.body.getReader !== 'function') {
        throw new Error('NO_STREAM');   // 没按流式回（或中间有代理把响应攒成了一坨）
      }
      var reader  = res.body.getReader();
      var decoder = new global.TextDecoder('utf-8');
      var buf     = '';
      var rawAll  = '';
      var final   = null;

      // 看门狗：链路层吊住连接（服务端卡死 / 中间代理不吐数据）时主动收摊。
      // 后端有卡死检测兜底，这里防的是「后端已结束但前端还在等」——那样轮次会永远停在
      // 「生成中」、评价按钮永远灰着，用户只能刷新页面。
      var idle = null;
      function arm() {
        if (idle) { global.clearTimeout(idle); }
        idle = global.setTimeout(function () {
          try { reader.cancel(); } catch (e) { /* 忽略 */ }
        }, 45000);
      }
      function disarm() {
        if (idle) { global.clearTimeout(idle); idle = null; }
      }

      function handleEvent(block) {
        var lines = block.replace(/\r/g, '').split('\n');
        var name = 'message';
        var dataLine = '';
        for (var i = 0; i < lines.length; i++) {
          if (lines[i].indexOf('event:') === 0) { name = lines[i].slice(6).trim(); }
          else if (lines[i].indexOf('data:') === 0) { dataLine += lines[i].slice(5).trim(); }
        }
        if (dataLine === '') { return; }
        var parsed = null;
        try { parsed = JSON.parse(dataLine); } catch (e) { return; }
        if (!parsed) { return; }
        if (name === 'chunk') {
          if (parsed.text) { onChunk(String(parsed.text)); }
        } else if (name === 'meta') {
          if (onMeta) { onMeta(parsed.id); }
        } else if (name === 'done' || name === 'error') {
          final = parsed;
        }
      }

      function pump() {
        return reader.read().then(function (r) {
          if (r.done) {
            disarm();
            if (!final && rawAll.trim() !== '') {
              try {
                var j = JSON.parse(rawAll.trim());
                if (j && typeof j.ok !== 'undefined') { final = j; }
              } catch (e) { /* 非 JSON，走下面的兜底 */ }
            }
            return final || { ok: false, msg: T('answerBroken', '回答中断，请再问一次') };
          }
          arm();   // 每收到数据就重置看门狗：卡的是「久无数据」，不是「总时长长」
          var text = decoder.decode(r.value, { stream: true });
          rawAll += text;
          buf += text;
          var idx;
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            handleEvent(buf.slice(0, idx));
            buf = buf.slice(idx + 2);
          }
          return pump();
        });
      }
      arm();   // 首次也要挂：卡死最可能发生在「服务端一直不吐第一片」的时候
      return pump();
    });
  }

  function scrollToSection(section) {
    try { section.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* 老浏览器/jsdom 无此 API */ }
  }

  /**
   * 只聚焦、不滚动。程序化 focus 的默认行为是把元素滚进视口（瞬间跳变）——
   * 流式收尾时回答刚把卡片撑高一大截，追问框往往已在视口外，此刻 ask.focus()
   * 会把页面一下跳走几百 px，观感就是「返回完了页面抖一下」（2026-09-16 稳定性测量复现：
   * Markdown 回答使卡片长高 238px，scrollY 单步跳变 486px，判据②当场变红）。
   * 不支持 options 的老浏览器会忽略参数按普通 focus 处理，无需特性探测。
   */
  function focusQuiet(node) {
    if (!node) { return; }
    try { node.focus({ preventScroll: true }); } catch (e) { node.focus(); }
  }

  function mount(section) {
    var main     = byHook(section, 'main');
    var form     = byHook(section, 'form');
    var chat     = byHook(section, 'chat');
    var turnsBox = byHook(section, 'turns');
    var done     = byHook(section, 'done');
    var text     = byHook(section, 'text');
    var send     = byHook(section, 'send');
    var ask      = byHook(section, 'ask');
    var askSend  = byHook(section, 'ask-send');
    var yes      = byHook(section, 'yes');
    var no       = byHook(section, 'no');
    var chips    = allHook(section, 'chip');
    var endpoint = section.getAttribute('data-endpoint') || '';
    var rateEndpoint = section.getAttribute('data-rate-endpoint') || '';
    /**
     * 首问表单按钮文案随入口切换：
     *   「AI 解读结果」进来是「问 AI」——用户意图是提问（表单已预填解读请求），与追问框的「追问」同链；
     *   「没解决」进来是「提交」——本质是反馈收集。busy 文案同理分开（「思考中…」更像在等 AI）。
     * rollbackTurn 退回表单时不传 mode：失败重试保持本次提问来源的文案，不换说法。
     */
    var sendLabels = { fb: T('submit', '提交'), ai: T('ask', '问 AI') };
    var sendBusy   = { fb: T('submitting', '提交中…'), ai: T('thinking', '思考中…') };
    var sendMode   = 'fb';
    var finished = false;   // 首问是否已出手：防重复点「解决了 / 没解决 / 提交」（追问不受限）
    var busy     = false;   // 有请求在飞（首问与追问共用）
    var turns    = [];      // 已完成的问答轮次 [{q,a}]，作为追问的 history

    /** 后端 AI 是否已配置：只切换文案措辞，提交行为完全一致 */
    function aiOn() { return section.getAttribute('data-ai') === '1'; }

    /**
     * AI 生成内容的隐式标识（《人工智能生成合成内容标识办法》2025-09-01 施行）。
     * 显式标识 = 页面上用户看得见的「由 AI 生成」免责条（answerTag）；
     * 隐式标识 = 机器可读的元数据——服务提供者 + 内容编号，写在轮次容器上。
     * 两者缺一不可：只有文本免责条时，抓取方/审计方拿不到这段内容是 AI 生成的证据。
     */
    function aiProvider() {
      var m = doc.querySelector ? doc.querySelector('meta[name="ai-content-provider"]') : null;
      return (m && m.getAttribute('content')) || '';
    }

    function markAiGenerated(node) {
      if (!node) { return; }
      node.setAttribute('data-ai-generated', '1');
      var p = aiProvider();
      if (p) { node.setAttribute('data-ai-provider', p); }
    }

    /**
     * 是否走 SSE 流式：后端开关 + 浏览器能力。
     * 故意在调用时判定而不是 mount 时缓存：① 冒烟可注入 ReadableStream 覆盖流式用例；
     * ② 老浏览器/被代理攒包的场景，能力缺失即自动回退到一次性 JSON，不必改代码。
     */
    function streamOn() {
      return section.getAttribute('data-stream') === '1'
        && typeof global.ReadableStream === 'function'
        && typeof global.TextDecoder === 'function'
        && typeof global.fetch === 'function';
    }

    function setBusy(on) {
      busy = on;
      if (yes) { yes.disabled = on; }
      if (no) { no.disabled = on; }
      if (send) {
        send.disabled = on;
        send.textContent = on ? sendBusy[sendMode] : sendLabels[sendMode];
      }
      if (askSend) {
        askSend.disabled = on;
        askSend.textContent = on ? T('following', '追问中…') : T('follow', '追问');
      }
      if (text) { text.readOnly = on; }
      if (ask) { ask.readOnly = on; }
      if (form) { form.setAttribute('aria-busy', on ? 'true' : 'false'); }
      if (chat) { chat.setAttribute('aria-busy', on ? 'true' : 'false'); }
    }

    /** mode：'ai' = AI 解读入口（「问 AI」）；'fb' = 没解决入口（「提交」）；不传 = 保持当前模式 */
    function showForm(prefill, mode) {
      if (mode) { sendMode = mode; }
      if (send) { send.textContent = sendLabels[sendMode]; }
      if (main) { main.hidden = true; }
      if (done) { done.hidden = true; }
      if (chat) { chat.hidden = true; }
      if (form) { form.hidden = false; }
      if (prefill && text) { text.value = prefill; }
      if (text) { text.focus(); }
    }

    function showChat() {
      if (main) { main.hidden = true; }
      if (form) { form.hidden = true; }
      if (done) { done.hidden = true; }
      if (chat) { chat.hidden = false; }
    }

    function showDone() {
      if (main) { main.hidden = true; }
      if (form) { form.hidden = true; }
      if (chat) { chat.hidden = true; }
      if (done) { done.hidden = false; }
    }

    /** 「再反馈一条」：整组复位回首问表单（会话清空、标签取消选中） */
    function resetAll() {
      finished = false;
      turns = [];
      if (turnsBox) { turnsBox.textContent = ''; }
      if (done) {
        done.hidden = true;
        done.textContent = '';
        done.className = 'tool-feedback-done';
      }
      setBusy(false);
      if (text) { text.value = ''; }
      if (ask) { ask.value = ''; }
      for (var i = 0; i < chips.length; i++) {
        chips[i].classList.remove('is-on');
        chips[i].setAttribute('aria-pressed', 'false');
      }
      showForm('', 'fb');   // 「再反馈一条」复位：回到反馈提交口径
    }

    function againRow(label) {
      var row = el('div', 'tool-feedback-again');
      var btn = el('button', 'tool-btn tool-btn--sm tool-btn--text');
      btn.type = 'button';
      btn.setAttribute('data-feedback-again', '');
      btn.appendChild(iconEl('rotate-cw'));
      btn.appendChild(span('', label));
      btn.addEventListener('click', resetAll);
      row.appendChild(btn);
      return row;
    }

    /**
     * 感谢态（无 AI 回答时的终态）
     * @param {string} msg
     * @param {boolean} withAgain 是否给「再反馈一条」。
     *   「解决了」分支必须传 false：用户刚说没问题，再引导他反馈是自问自答。
     * @param {string} [tone] 'warn' = 琥珀色框。用于「额度用完 / AI 故障」这类**不是成功**的说明——
     *   绿框配「额度用完了」是语义反的（与 👎 用琥珀同一个道理）。
     */
    function finishThanks(msg, withAgain, tone) {
      if (!done) { return; }
      done.textContent = '';
      done.className = 'tool-feedback-done is-thanks';
      var box = el('div', tone === 'warn' ? 'tool-feedback-thanks is-warn' : 'tool-feedback-thanks');
      var line = el('p', 'tool-feedback-done-line');
      line.appendChild(iconEl(tone === 'warn' ? 'alert-circle' : 'check-circle'));
      line.appendChild(span('', msg || T('thanksLong', '感谢反馈！我们会认真查看每一条意见，持续改进。')));
      box.appendChild(line);
      done.appendChild(box);
      if (withAgain) { done.appendChild(againRow(T('again', '再反馈一条'))); }
      showDone();
    }

    /**
     * 评价行：👍/👎 落库（唯一能证伪提示词的数据）
     * @param {function():number} idOf 惰性取记录 id。回答开始时 id 还不存在（流式要等 meta 事件），
     *   所以传取值函数而不是值——这样这一行能在开始时就把高度占住，终态只解锁、不加高。
     *   生成即 pending（按钮 disabled）：回答没出完不能打分，半截回答也不该被评价。
     */
    function rateRow(idOf) {
      var row = el('div', 'tool-feedback-rate is-pending');
      row.setAttribute('data-feedback-rate', '');
      row.appendChild(span('tool-feedback-rate-q', T('rateAsk', '这个回答有用吗？')));
      row.appendChild(rateBtn(idOf, 1, T('helpful', '有用'), 'thumb-up'));
      row.appendChild(rateBtn(idOf, 0, T('unhelpful', '没用'), 'thumb-down'));
      return row;
    }

    function rateBtn(idOf, helpful, label, iconName) {
      var btn = el('button', 'tool-btn tool-btn--sm tool-feedback-rate-btn');
      btn.type = 'button';
      btn.disabled = true;   // 由 finishTurn 解锁（那时回答才完整、id 才有）
      btn.setAttribute(helpful === 1 ? 'data-feedback-rate-up' : 'data-feedback-rate-down', '');
      btn.appendChild(iconEl(iconName));
      btn.appendChild(span('', label));
      btn.addEventListener('click', function () { sendRate(idOf(), helpful, btn); });
      return btn;
    }

    function sendRate(id, helpful, btn) {
      var row = btn.parentNode;
      if (!id) { return; }   // 还没拿到记录 id（生成中）：按钮此时也是禁用的，这里只是双保险
      // 注意用 hasAttribute 判断标记属性：这些钩子属性的值是空串，getAttribute 取回来是 ''（falsy），
      // 写成 !row.getAttribute(...) 会把每一次评价都静默挡掉（踩过）
      if (!rateEndpoint || !row || !row.hasAttribute('data-feedback-rate') || row.getAttribute('data-rated')) { return; }
      row.setAttribute('data-rated', '1');
      postJSON(rateEndpoint, { id: id, helpful: helpful }).then(function (res) {
        if (res && res.ok) {
          markRated(row, helpful);
          toast(T('rated', '已记录，谢谢！'));
        } else {
          row.removeAttribute('data-rated');
          toast(failMsg(res, 'rateFail'));
        }
      }).catch(function () {
        row.removeAttribute('data-rated');
        toast(T('rateFail', '评价失败，请稍后再试'));
      });
    }

    function markRated(row, helpful) {
      row.textContent = '';
      // is-down 让 CSS 换色系：👎 用琥珀而不是绿色（绿框配「已记录没用」语义是反的）
      row.className = helpful === 1 ? 'tool-feedback-rate is-rated' : 'tool-feedback-rate is-rated is-down';
      row.appendChild(iconEl(helpful === 1 ? 'thumb-up' : 'thumb-down'));
      row.appendChild(span('', helpful === 1 ? T('ratedUp', '已记录「有用」，谢谢！') : T('ratedDown', '已记录「没用」，我们会针对这类问题改进。')));
    }

    /** 免责条（回答态与「生成中」共用：终态才插入它会让卡片突然长高，见 startTurn 注释） */
    function answerTag() {
      var tag = el('p', 'tool-feedback-answer-tag');
      tag.appendChild(iconEl('alert-circle'));
      tag.appendChild(span('tool-feedback-answer-tag-text',
        T('disclaimer', '由 AI 生成，仅供参考，请以工具的实际计算结果为准；如仍有疑问，可在下方继续追问。')));
      return tag;
    }

    /**
     * 把光标放到「最后一段文字」的末尾。
     *
     * 必须放进块级文字容器（p / li）里，不能 append 到回答容器上：
     * 行内元素夹在块级元素之间会生成匿名块 —— 光标另起一行吊在回答下方，
     * 还白占一整行高度（真浏览器实测：容器 185px → 去掉光标 160px）。伪元素同理。
     * 光标两种状态都保留（只换颜色），避免收尾摘掉它导致末行重排。
     */
    function placeCaret(turn) {
      if (!turn.caret) { return; }
      var kids = turn.body.children;
      var target = null;
      for (var i = kids.length - 1; i >= 0; i--) {
        if (String(kids[i].className || '').indexOf('tool-feedback-caret') > -1) { continue; }
        target = kids[i];
        break;
      }
      var tag = target ? String(target.tagName).toLowerCase() : '';
      if (tag === 'ul' || tag === 'ol') { target = target.lastElementChild || target; }
      if (tag === 'hr') { target = null; }   // hr 是空元素，装不了东西，退回容器
      (target || turn.body).appendChild(turn.caret);
    }

    /**
     * 把正文渲染进回答位。
     * Markdown 子集交给 ui.js 的 md()（AI 实际会输出 ## 标题 / **加粗** / - 列表 / --- 分隔线）；
     * 缺少 md（例如旧缓存里的 ui.js）时退回纯文本，只是难看，不会白屏。
     */
    function renderBody(turn, text) {
      var UI = global.ToolUI || {};
      turn.body.className = 'tool-feedback-answer';
      // 流式中间态走乐观渲染（未闭合的 **/`、未收完的 ---/## 直接按最终形态显示），
      // 收尾（finishTurn 置 streaming=false 后）走严格口径——正常闭合的文本两种口径渲染一致，零跳变
      if (typeof UI.md === 'function') { UI.md(turn.body, text, { optimistic: turn.streaming }); } else { turn.body.textContent = text; }
      placeCaret(turn);
    }

    /**
     * 单次大增量平滑展开：代理攒包 / 非流式降级时整段一次性插入（占位一行 → 几百 px），
     * 下方内容瞬移就是「抖一下」。增长超过阈值时用高度动画把「跳」变「滑」。
     * 正常流式分片增量小（几十 px），不触发；jsdom 等无布局环境测得 0 高，自然跳过。
     */
    function smoothGrow(node, prevH) {
      try {
        if (!(prevH > 0) || typeof node.animate !== 'function') { return; }
        var nextH = node.getBoundingClientRect().height;
        if (nextH - prevH < 80) { return; }
        node.style.overflow = 'hidden';
        var anim = node.animate(
          [{ height: prevH + 'px' }, { height: nextH + 'px' }],
          { duration: 240, easing: 'ease-out' }
        );
        function unlock() { if (node.style.overflow === 'hidden') { node.style.overflow = ''; } }
        anim.onfinish = unlock;
        anim.oncancel = unlock;   // 下一片又大增会打断重放，取消时也必须归还 overflow
      } catch (e) { /* 老环境不动画，直接跳变（与旧行为一致） */ }
    }

    /** 生成中占位：三点脉冲 + 文案，与光标同处回答位 */
    function renderPending(turn) {
      turn.body.className = 'tool-feedback-answer is-pending';
      turn.body.textContent = '';
      var p = el('p', 'tool-md-p');
      p.appendChild(dotsEl());
      p.appendChild(span('tool-feedback-pending-text',
        aiOn() ? T('generating', 'AI 正在读你的输入与结果，生成中…') : T('submittingLong', '正在提交…')));
      turn.body.appendChild(p);
      placeCaret(turn);
    }

    /**
     * 起一轮：问题立刻上屏，回答区先放占位，**免责条与评价行从一开始就占住高度**。
     *
     * 这里是为了根治「流式结束后页面抖一下」：真浏览器实测（scripts/check-stream-stability.mjs）
     * 原实现终态那一瞬卡片一次长高 78px（插入免责条 + 评价行），下方内容整体下移，观感就是跳一下。
     * 现在终态只做两件不改变布局的事：解锁评价按钮、把占位换成正文。
     */
    function startTurn(question) {
      if (!turnsBox) { return null; }
      var root = el('div', 'tool-feedback-turn is-streaming');
      markAiGenerated(root);

      var quote = el('p', 'tool-feedback-quote');
      quote.appendChild(span('tool-feedback-quote-label', T('yourQuestion', '你的问题：')));
      quote.appendChild(doc.createTextNode(question));
      root.appendChild(quote);

      // 生成中的指示器就放在回答位里（内容将要出现的地方），而不是卡片底部再挂一行：
      // 独立等待行在首片到达时要消失，那一瞬又是一次 28px 位移（真浏览器实测）。
      var body = el('div', 'tool-feedback-answer is-pending');
      root.appendChild(body);

      // 注意：光标创建时不带 is-on —— 生成中的动效由三点脉冲负责，两个一起闪会显得吵；
      // 首个文本分片到达时才点亮（见 appendChunk）
      var turn = { root: root, body: body, text: '', id: 0, pending: true, streaming: true, caret: el('span', 'tool-feedback-caret') };
      renderPending(turn);
      root.appendChild(answerTag());
      turn.rate = rateRow(function () { return turn.id; });
      root.appendChild(turn.rate);

      turnsBox.appendChild(root);
      // 流式期间 aria-busy：否则约 50 次分片写入会让读屏把未完的句子逐片播报
      turnsBox.setAttribute('aria-busy', 'true');
      turnsBox.scrollTop = turnsBox.scrollHeight;
      return turn;
    }

    /**
     * 追加一个流式分片：对「累积后的整段」重新渲染。
     * 为什么不逐片拼 DOM：Markdown 有跨分片的成对标记（一对 ** 很可能被切成两半），
     * 只有对整段重新解析才能正确闭合；累积文本只有几百字，整段重渲染的开销可以忽略。
     */
    function appendChunk(turn, delta) {
      if (!turn) { return; }
      turn.pending = false;
      if (turn.caret) { turn.caret.className = 'tool-feedback-caret is-on'; }   // 开始出字 → 光标亮起
      var prevH = 0;
      try { prevH = turn.body.getBoundingClientRect().height; } catch (e) { /* jsdom 等无布局环境恒 0 */ }
      turn.text = (turn.text || '') + delta;
      renderBody(turn, turn.text);
      smoothGrow(turn.body, prevH);
      if (turnsBox) { turnsBox.scrollTop = turnsBox.scrollHeight; }
    }

    /** 收尾一轮：解锁评价 + 换正文；answer 为空则整轮回滚（不留空壳） */
    function finishTurn(turn, answer, id) {
      if (!turn) { return null; }
      if (!answer) {
        if (turn.root.parentNode) { turn.root.parentNode.removeChild(turn.root); }
        if (turnsBox) { turnsBox.removeAttribute('aria-busy'); }
        return null;
      }
      turn.id = id || turn.id;
      if (turn.id) { turn.root.setAttribute('data-ai-content-id', String(turn.id)); }
      turn.pending = false;
      turn.streaming = false;   // 先摘流式标记再渲染：终态走严格口径（乐观中间态若与严格结果有差异，此刻一并归位）
      turn.text = answer;
      renderBody(turn, answer);   // 内联格式全走 createElement/textContent，天然防注入
      if (turn.caret) { turn.caret.className = 'tool-feedback-caret'; }   // 光标停（保留占位不留位移）
      turn.root.className = 'tool-feedback-turn';   // 摘掉 is-streaming
      if (turn.rate) {
        if (turn.id) {
          turn.rate.className = 'tool-feedback-rate';
          var btns = turn.rate.querySelectorAll('button');
          for (var i = 0; i < btns.length; i++) { btns[i].disabled = false; }
        } else {
          // 没有记录 id 就评不了（rate 接口按 id 锚定）：宁可不显示这一行，
          // 也不要给一个「点了没反应」的按钮 —— 那正是本轮刚修掉的那类 bug
          if (turn.rate.parentNode) { turn.rate.parentNode.removeChild(turn.rate); }
          turn.rate = null;
        }
      }
      if (turnsBox) {
        turnsBox.removeAttribute('aria-busy');
        turnsBox.scrollTop = turnsBox.scrollHeight;
      }
      return turn;
    }

    /** 失败回滚：一个字都没收到就撤掉空轮次；首问还要退回表单（输入仍在，可直接重试） */
    function rollbackTurn(turn, isFirst) {
      if (!turn || String(turn.text || '') !== '' || !turn.root.parentNode) { return; }
      turn.root.parentNode.removeChild(turn.root);
      if (turnsBox) { turnsBox.removeAttribute('aria-busy'); }
      if (isFirst) { showForm(''); }   // 不传 mode：失败重试保持提问来源的按钮文案
    }

    /** 追问历史：只带最近 3 轮（服务端还会再截断一次） */
    function history() {
      var out = [];
      for (var i = 0; i < turns.length; i++) {
        if (turns[i].a) { out.push({ q: turns[i].q, a: turns[i].a }); }
      }
      return out.slice(-3);
    }

    /**
     * 提问主链路（首问与追问共用）
     * @param {string} raw 问题文本
     * @param {boolean} isFirst 是否首问（首问成功即进终态，防重复提交）
     */
    function askQuestion(raw, isFirst) {
      // isFirst 还要看终态标志：首问成功后表单虽已收起，但按钮监听仍在，
      // 少这道守卫就存在「对同一个问题重复发问、重复扣 AI 预算」的后门
      if (busy || (isFirst && finished)) { return; }
      var q = String(raw == null ? '' : raw).trim();
      if (q === '') {
        toast(T('askPlaceholder', '写一句你想解决的问题，AI 才知道要帮什么'));
        return;
      }
      setBusy(true);

      var payload = {
        slug: pageSlug(),
        solved: 0,
        question: q.slice(0, 500),
        context: collectContext(section),
        history: history()
      };

      // 提交即切会话态并把问题打上屏：别等首片到达才切——那会儿用户正盯着在读，切换更刺眼；
      // 顺带把「点了提交没反应」的空窗也填掉（首个文本分片实测要 5~6s）。
      // 只对「后端真会调 AI」的情况切换：未配 AI 时答案注定为空，先亮会话再塌回感谢态更糟。
      if (isFirst && aiOn()) { showChat(); }
      var turn = startTurn(q);

      // 流式分支：分片到达就上屏，首片到达即收起等待态（屏幕上已经在出字了，
      // 还挂着「AI 正在读…」自相矛盾），但 busy 保持 true —— 出字期间不许再发问。
      var pending = streamOn()
        ? postStream(endpoint, payload, function (delta) {
            appendChunk(turn, delta);
          }, function (id) {
            if (turn && id) { turn.id = id; }   // meta 事件先到：评价要用的记录 id
          })
        : postJSON(endpoint, payload);

      pending.then(function (res) {
        setBusy(false);
        if (!res || !res.ok) {
          // 失败但**已经吐出内容**：用户屏幕上看到的字必须留住，而且这一轮必须收尾——
          // 否则它会永远停在「生成中」，评分按钮一直禁用（2026-09-14 用户实测：
          // 回答已完整输出，仅因传输层异常走了 error 分支，用户看到「回答出来了但按钮是灰的」）。
          if (turn && String(turn.text || '') !== '') {
            finished = true;
            turns.push({ q: q, a: String(res.answer || turn.text) });
            showChat();
            finishTurn(turn, String(res.answer || turn.text), res.id);
            toast(failMsg(res, 'answerMaybeIncomplete'));
            return;
          }
          rollbackTurn(turn, isFirst);
          toast(failMsg(res, 'submitFail'));
          return;
        }
        if (!res.answer) {
          // 无 AI 回答：如实说明是「额度用完 / AI 故障 / 未配置」，首问进终态、追问只提示
          var why = skipMsg(res.skip);
          if (turn && turn.root.parentNode) { turn.root.parentNode.removeChild(turn.root); }
          if (turnsBox) { turnsBox.removeAttribute('aria-busy'); }
          if (isFirst) {
            finished = true;
            finishThanks(why || T('solvedThanks', '已收到反馈，我们会认真查看并持续改进，感谢你的帮助！'), true, why ? 'warn' : '');
          } else {
            toast(why || T('aiFailShort', 'AI 暂时没能回答，稍后再试试'));
          }
          return;
        }
        finished = true;
        var answer = String(res.answer);
        if (isFirst) { if (text) { text.value = ''; } }
        if (ask) { ask.value = ''; }
        turns.push({ q: q, a: answer });
        showChat();
        finishTurn(turn, answer, res.id);
        if (res.truncated) { toast(T('answerMaybeIncomplete', '回答可能没输出完整，可再问一次')); }
        focusQuiet(ask);   // 不能裸 focus：回答把卡片撑高后追问框常在视口外，focus 会把页面瞬移滚走
      }).catch(function () {
        setBusy(false);
        rollbackTurn(turn, isFirst);
        toast(T('submitFail', '提交失败，请稍后再试'));
      });
    }

    /** 快捷原因标签：点一下把整句描述追加/撤回（用户只写「不对」时 AI 无从下手） */
    function toggleChip(chip) {
      if (!text) { return; }
      var phrase = chip.getAttribute('data-feedback-chip') || String(chip.textContent || '').trim();
      var cur = String(text.value || '');
      if (chip.classList.contains('is-on')) {
        var idx = cur.indexOf(phrase);
        if (idx >= 0) { cur = cur.slice(0, idx) + cur.slice(idx + phrase.length); }
        cur = cur.replace(/^[；;，,、\s]+/, '').replace(/[；;、,，\s]+$/, '');
        chip.classList.remove('is-on');
        chip.setAttribute('aria-pressed', 'false');
      } else {
        var base = cur.replace(/\s+$/, '');
        cur = base === '' ? phrase : base + '；' + phrase;
        chip.classList.add('is-on');
        chip.setAttribute('aria-pressed', 'true');
      }
      text.value = cur.slice(0, 500);
      text.focus();
    }

    /** 「AI 解读结果」按钮：各结果区一份，点击行为完全一致 */
    function makeEntryBtn() {
      /* 工具段 aiEntry 覆盖（「一页纸决策」栏目 AI 疏导标配，#128 引入）：
         label/preset 任一非空则覆盖通用文案——疏导场景的入口措辞与预填问题
         与「解读结果」不同（不评判、理思路），由语言包按工具配置。
         取段口径与 T() 相同：当前语言 → 组基准语言 → 空对象回通用文案 */
      var slug = pageSlug();
      var packsRoot = window.ToolI18n || {};
      var seg = (packsRoot[window.TOOLS_LANG] || {})[slug] || (packsRoot[window.TOOLS_FALLBACK_LANG] || {})[slug] || {};
      var entry = (seg && seg.aiEntry) || {};
      var entryLabel = (typeof entry.label === 'string' && entry.label !== '')
        ? entry.label : T('readResult', 'AI 解读结果');
      var entryPreset = (typeof entry.preset === 'string' && entry.preset !== '')
        ? entry.preset : T('presetQuestion', '请帮我解读当前结果：它是否正常？结合我的输入说说该怎么理解，以及常见的误用有哪些。');
      var btn = el('button', 'tool-btn tool-btn--sm tool-result-ai');
      btn.type = 'button';
      btn.setAttribute('data-feedback-ai-tip', '');
      btn.appendChild(iconEl('sparkles'));
      btn.appendChild(span('', entryLabel));
      btn.addEventListener('click', function () {
        if (!collectResults(section).length) {
          toast(T('needResultFirst', '先在上面算出结果，AI 才知道要解读什么'));
          return;
        }
        if (finished) {
          // 已经问过：把卡片滚过来，让他接着追问（新结果也能问）
          scrollToSection(section);
          if (chat && !chat.hidden && ask) { focusQuiet(ask); }   // 滚动交给 scrollIntoView，focus 只负责焦点
          return;
        }
        showForm(entryPreset, 'ai');
        scrollToSection(section);
      });
      return btn;
    }

    /**
     * 结果区常驻入口：把 AI 从「答不上来才找我」搬到「结果旁边」。
     * 运行时注入，不动 171 个 partial；重复调用幂等（已有入口就跳过）。
     * 多结果区工具（power/loan/ratio 等多面板）每个结果区都要有入口——
     * 只注入第一个的话，切到其他 tab 后入口就「消失」了（2026-09-15 用户实测踩到）。
     */
    function ensureResultEntry() {
      var hosts = doc.querySelectorAll('.tool-result');
      for (var i = 0; i < hosts.length; i++) {
        var host = hosts[i];
        if (section.contains(host) || host.querySelector('[data-feedback-ai-tip]')) { continue; }
        host.appendChild(makeEntryBtn());
      }
    }

    for (var c = 0; c < chips.length; c++) {
      (function (chip) {
        chip.addEventListener('click', function () { toggleChip(chip); });
      })(chips[c]);
    }

    if (yes) {
      yes.addEventListener('click', function () {
        if (finished || busy) { return; }
        finished = true;
        setBusy(true);
        postJSON(endpoint, {
          slug: pageSlug(), solved: 1, question: '', context: '', history: []
        }).then(function (res) {
          setBusy(false);
          if (res && res.ok) { finishThanks(T('thanksShort', '感谢反馈！'), false); }
          else { finished = false; toast(failMsg(res, 'submitFail')); }
        }).catch(function () {
          setBusy(false);
          finished = false;
          toast(T('submitFail', '提交失败，请稍后再试'));
        });
      });
    }
    if (no) {
      no.addEventListener('click', function () {
        if (finished || busy) { return; }
        showForm('', 'fb');
      });
    }
    if (send) {
      send.addEventListener('click', function () { askQuestion(text ? text.value : '', true); });
    }
    if (askSend) {
      askSend.addEventListener('click', function () { askQuestion(ask ? ask.value : '', false); });
    }

    // 结果区入口注入：工具重绘结果区可能把入口带走，故在交互后幂等补挂（同状态零写入）
    ensureResultEntry();
    var toolRoot = doc.querySelector('.tool-card--tool');
    if (toolRoot) {
      toolRoot.addEventListener('input', ensureResultEntry);
      toolRoot.addEventListener('click', ensureResultEntry);
    }
  }

  var sections = doc.querySelectorAll('[data-feedback]');
  for (var i = 0; i < sections.length; i++) { mount(sections[i]); }

})(typeof window !== 'undefined' ? window : globalThis);
